import { kAgentV2RouterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { nanoid } from 'nanoid'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import * as Sentry from '@sentry/browser'
import { formatUnknownError, getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { LrrContentStatusResponseSchema } from '../schema/lrr-reference-viewer.schema'

/**
 * Whether the law behind a cited reference has changed or gone since it was cited.
 *
 * Returns null rather than throwing for every outcome that should simply render no badge.
 * A 404 is expected, not exceptional: the reference may be gone, its conversation deleted,
 * or it may belong to a matter this user cannot see — the backend answers not-found rather
 * than forbidden, so a shared link never confirms another matter's reference exists.
 * Reporting those to Sentry would bury real failures in noise; every other failing status is
 * captured, as is a body that will not parse or does not match the schema. Every one of those
 * still resolves to null: a badge lookup must never degrade the document it annotates.
 *
 * A status the backend cannot determine — no stored digest, or a search index it could not
 * reach — arrives as a 200 carrying a null status, and likewise renders nothing.
 */
export async function getLrrContentStatusForStandaloneViewer(referenceId: string): Promise<string | null> {
  const sentry_transaction_id = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw Error(kErrorCodeAuthError)

  const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2RouterApiBasePath}/references/${referenceId}/lrr-content-status`

  const response = await fetch(apiUrl, {
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Transaction-Id': sentry_transaction_id,
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
  })

  if (response.status === 404) return null

  if (!response.ok) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })
      Sentry.captureException(new Error(`LRR content status fetch error - status: ${response.status}`), {
        extra: { requestUrl: apiUrl, responseStatus: response.status, onLine: navigator.onLine },
      })
    })
    return null
  }

  // readResponseJson rather than response.json(): an unparseable body must reach the same
  // diagnostic as a schema-invalid one, not reject past it and get swallowed as "no badge".
  const { data, parseError } = await readResponseJson(response)

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })
      Sentry.captureException(new Error('LRR content status response JSON parse failed'), {
        extra: { ...getResponseDebugContext(response, apiUrl), parseError: formatUnknownError(parseError) },
      })
    })
    return null
  }

  const parsed = LrrContentStatusResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })
      Sentry.captureException(new Error('LRR content status response schema validation failed'), {
        extra: { ...getResponseDebugContext(response, apiUrl), issues: parsed.error.issues.map((issue) => issue.path.join('.')) },
      })
    })
    return null
  }

  return parsed.data.content_status ?? null
}
